/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useEffect, useRef, type ReactElement } from 'react';

const FLY_COIN_SIZE_PX = 48;
const HALF = 2;
const CENTER_OFFSET_Y = -60;

interface FlyingCoinProperties {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  durationMs: number;
  coinIconSrc: string;
}

function FlyingCoin({
  startX,
  startY,
  endX,
  endY,
  durationMs,
  coinIconSrc,
}: FlyingCoinProperties): ReactElement {
  const reference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = reference.current;
    if (!element) return () => {};
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        element.style.left = `${endX}px`;
        element.style.top = `${endY}px`;
      });
    });
    return () => cancelAnimationFrame(id);
  }, [endX, endY]);

  return (
    <div
      ref={reference}
      className="z-coins-overlay pointer-events-none fixed transition-all ease-out"
      style={{
        width: FLY_COIN_SIZE_PX,
        height: FLY_COIN_SIZE_PX,
        left: startX,
        top: startY,
        transitionDuration: `${durationMs}ms`,
      }}
    >
      <img
        src={coinIconSrc}
        alt=""
        className="h-full w-full object-contain"
        width={FLY_COIN_SIZE_PX}
        height={FLY_COIN_SIZE_PX}
      />
    </div>
  );
}

export interface FlyingCoinsProperties {
  centerX: number;
  centerY: number;
  targetRect: DOMRect;
  durationMs: number;
  coinIconSrc: string;
}

export default function FlyingCoins({
  centerX,
  centerY,
  targetRect,
  durationMs,
  coinIconSrc,
}: FlyingCoinsProperties): ReactElement {
  const targetCenterX = targetRect.left + targetRect.width / HALF - FLY_COIN_SIZE_PX / HALF;
  const targetCenterY = targetRect.top + targetRect.height / HALF - FLY_COIN_SIZE_PX / HALF;

  const startX = centerX - FLY_COIN_SIZE_PX / HALF;
  const startY = centerY - FLY_COIN_SIZE_PX / HALF + CENTER_OFFSET_Y;

  return (
    <FlyingCoin
      startX={startX}
      startY={startY}
      endX={targetCenterX}
      endY={targetCenterY}
      durationMs={durationMs}
      coinIconSrc={coinIconSrc}
    />
  );
}
